import { api } from '../../lib/api-client';

export type LeadStage =
  | 'NEW_LEAD'
  | 'CONTACTED'
  | 'INTERESTED'
  | 'TEST_RIDE'
  | 'QUOTATION_SENT'
  | 'BOOKING_RECEIVED'
  | 'LOAN_PROCESSING'
  | 'APPROVED'
  | 'READY_DELIVERY'
  | 'DELIVERED'
  | 'LOST';

export type LeadSource =
  'META' | 'WEBSITE' | 'WHATSAPP' | 'WALK_IN' | 'ROADSHOW' | 'REFERRAL' | 'REPEAT' | 'MANUAL';

export type ActivityType =
  'CALL' | 'WHATSAPP' | 'EMAIL' | 'VISIT' | 'TEST_RIDE' | 'QUOTATION' | 'FOLLOW_UP' | 'NOTE';

export interface Lead {
  uid: string;
  leadNo: string;
  name: string;
  phone: string;
  email: string | null;
  source: LeadSource;
  campaign: string | null;
  stage: LeadStage;
  interestModel: string | null;
  assignedAdvisor: string | null;
  assignedAdvisorUid: string | null;
  firstResponseAt: string | null;
  respondedWithinTarget: boolean | null;
  nextActionAt: string | null;
  isOverdue: boolean;
  lostReason: string | null;
  lostCompetitor: string | null;
  branchCode: string | null;
  createdAt: string;
}

export interface LeadPage {
  items: Lead[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ListLeadsParams {
  q?: string;
  stage?: LeadStage;
  mine?: boolean;
  overdue?: boolean;
  cursor?: string;
}

export function listLeads(params: ListLeadsParams): Promise<LeadPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.stage !== undefined) search.set('stage', params.stage);
  if (params.mine === true) search.set('mine', 'true');
  if (params.overdue === true) search.set('overdue', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<LeadPage>(`/leads${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getLead(uid: string): Promise<Lead> {
  return api<Lead>(`/leads/${uid}`);
}

export function overdueCount(): Promise<{ count: number }> {
  return api<{ count: number }>('/leads/overdue/count');
}

export function createLead(input: {
  name: string;
  phone: string;
  email?: string;
  source: LeadSource;
  campaign?: string;
  interestModelUid?: string;
}): Promise<Lead> {
  return api<Lead>('/leads', { method: 'POST', body: input });
}

export function logActivity(
  uid: string,
  input: { type: ActivityType; outcome?: string; note?: string; nextActionAt?: string },
): Promise<{ uid: string }> {
  return api<{ uid: string }>(`/leads/${uid}/activities`, { method: 'POST', body: input });
}

export function changeStage(
  uid: string,
  input: { stage: LeadStage; reason?: string; lostReason?: string; lostCompetitor?: string },
): Promise<Lead> {
  return api<Lead>(`/leads/${uid}/transitions/stage`, { method: 'POST', body: input });
}

export type WhatsAppTemplate = 'lead.first_contact' | 'lead.follow_up' | 'lead.test_ride';

/**
 * Asks the API to compose the message and log that we opened it.
 *
 * The message is written server-side, not here (CLAUDE.md §13e). Two reasons:
 * the same template is what a real WhatsApp Business API will send when NKS has
 * one, and the activity gets logged as part of the same call — an advisor
 * should not have to remember to record a follow-up they just sent.
 */
export function composeWhatsApp(
  uid: string,
  templateCode: WhatsAppTemplate,
): Promise<{ message: string; link: string; activityUid: string }> {
  return api(`/leads/${uid}/messages/whatsapp`, { method: 'POST', body: { templateCode } });
}

export function exportLeadsCsv(params: ListLeadsParams): Promise<string> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.mine === true) search.set('mine', 'true');
  if (params.overdue === true) search.set('overdue', 'true');
  const qs = search.toString();
  return api<string>(`/leads/export/csv${qs.length > 0 ? `?${qs}` : ''}`);
}
